import React, { useState } from "react";
import { Box, Chip, useTheme } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../../components/Header";
import StatBox from "../../../components/StatBox";
import {
  AccountBalanceOutlined,
  LockOutlined,
  ReceiptLongOutlined,
} from "@mui/icons-material";
import { format } from "date-fns";
import {
  useGetCompanyWalletQuery,
  useGetCompanyWalletStatementsQuery,
} from "../../../state/api";

const CompanyWallet = () => {
  const theme = useTheme();
  const { data: wallet, isLoading: walletLoading } = useGetCompanyWalletQuery();
  const { data: statementData, isLoading: statementsLoading } =
    useGetCompanyWalletStatementsQuery();
  const [pageSize, setPageSize] = useState(20);

  const statements = (statementData && statementData.statementList) || [];

  const columns = [
    {
      field: "createdAt",
      headerName: "Date",
      flex: 0.5,
      renderCell: (params) => format(new Date(params.value), "yyyy-MM-dd HH:mm"),
    },
    {
      field: "narration",
      headerName: "Narration",
      flex: 0.8,
    },
    {
      field: "user",
      headerName: "User",
      flex: 0.7,
      renderCell: (params) => {
        const user = params.row.userId;
        if (!user || typeof user !== "object") return "—";
        return (
          <Box>
            <Box sx={{ fontSize: "0.85rem" }}>{user.fullname}</Box>
            <Box sx={{ fontSize: "0.7rem", opacity: 0.7 }}>{user.email}</Box>
          </Box>
        );
      },
    },
    {
      field: "debit",
      headerName: "Debit (Paid Out)",
      flex: 0.5,
      renderCell: (params) =>
        params.value ? `NRs ${Number(params.value).toLocaleString()}` : "—",
    },
    {
      field: "credit",
      headerName: "Credit (Received)",
      flex: 0.5,
      renderCell: (params) =>
        params.value ? `NRs ${Number(params.value).toLocaleString()}` : "—",
    },
    {
      field: "amount",
      headerName: "Balance After",
      flex: 0.5,
      renderCell: (params) => `NRs ${Number(params.value).toLocaleString()}`,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.4,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value}
          sx={{
            textTransform: "capitalize",
            backgroundColor:
              params.value === "success" ? "#00c9a722" : "#de443722",
            color: params.value === "success" ? "#00c9a7" : "#de4437",
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      field: "remarks",
      headerName: "Remarks",
      flex: 0.8,
      renderCell: (params) => params.value || "—",
    },
  ];

  return (
    <Box m="1.5rem 2.5rem">
      <Header
        title="COMPANY WALLET"
        subtitle="Platform balance and full transaction history"
      />

      <Box
        mt="20px"
        display="grid"
        gridTemplateColumns={{ xs: "1fr", md: "repeat(3, 1fr)" }}
        gap="20px"
      >
        <StatBox
          title="Available Balance"
          value={wallet && wallet.creditPoints}
          caption="Current company wallet balance"
          loading={walletLoading}
          accent="#00c9a7"
          format="currency"
          icon={<AccountBalanceOutlined />}
        />
        <StatBox
          title="Locked Funds"
          value={wallet && wallet.lockedCreditPoints}
          caption="Reserved, not yet spendable"
          loading={walletLoading}
          accent="#ffda85"
          format="currency"
          icon={<LockOutlined />}
        />
        <StatBox
          title="TDS Withheld"
          value={wallet && wallet.tdsAmount}
          caption="Tax deducted at source (lifetime)"
          loading={walletLoading}
          accent="#a370d1"
          format="currency"
          icon={<ReceiptLongOutlined />}
        />
      </Box>

      <Box
        mt="20px"
        height="60vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.background.alt,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none",
          },
        }}
      >
        <DataGrid
          loading={statementsLoading}
          getRowId={(row) => row._id}
          rows={statements}
          columns={columns}
          pagination
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Box>
    </Box>
  );
};

export default CompanyWallet;
